import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";

import { FIRM } from "@/lib/firm";
import { submitGrannyFlatEnquiry } from "@/lib/granny-flat-enquiries.functions";

const RELATIONSHIPS = [
  { value: "older-person", label: "I'm the older person moving in" },
  { value: "adult-child", label: "I'm the adult child / property owner" },
  { value: "other-family", label: "Another family member" },
  { value: "attorney-or-carer", label: "Attorney, guardian or carer" },
  { value: "adviser", label: "Accountant, adviser or agent" },
] as const;

const AGE_RANGES = [
  { value: "under-60", label: "Under 60" },
  { value: "60-69", label: "60 – 69" },
  { value: "70-79", label: "70 – 79" },
  { value: "80-plus", label: "80 or over" },
  { value: "not-applicable", label: "Prefer not to say" },
] as const;

const FUNDING = [
  { value: "sale-of-home", label: "Sale of the older person's home" },
  { value: "savings", label: "Savings" },
  { value: "superannuation", label: "Superannuation" },
  { value: "inheritance", label: "An inheritance" },
  { value: "loan-or-mortgage", label: "A loan or mortgage" },
  { value: "not-decided", label: "Not decided yet" },
] as const;

const MEETINGS = [
  { value: "in-person", label: "In person at Golden Beach" },
  { value: "phone", label: "By phone" },
  { value: "video", label: "Video call" },
] as const;

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

export function GrannyFlatEnquiryForm({
  heading = "Request a granny flat consultation",
}: {
  heading?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      relationship: String(fd.get("relationship") ?? "older-person"),
      ageRange: String(fd.get("ageRange") ?? "not-applicable"),
      fundingSource: String(fd.get("fundingSource") ?? "not-decided"),
      contributionAmount: String(fd.get("contributionAmount") ?? ""),
      propertySuburb: String(fd.get("propertySuburb") ?? ""),
      meetingPreference: String(fd.get("meetingPreference") ?? "in-person"),
      message: String(fd.get("message") ?? ""),
      ...getAttribution(pathname),
    };

    const nextErrors: { name?: string; email?: string } = {};
    if (payload.name.trim().length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email.trim()))
      nextErrors.email = "Please enter a valid email address";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    try {
      await submitGrannyFlatEnquiry({ data: payload as never });
      setSent(true);
      void navigate({ to: "/thank-you", search: { type: "granny-flat", matter: "granny-flat" } });
      form.reset();
      toast.success("Thanks — we've received your granny flat enquiry.");
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-sm border border-border bg-card p-8 text-center">
        <h2 className="font-serif text-2xl text-foreground">Thank you</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We have your details and will call to arrange a time. Bring the granny flat questions
          checklist to the appointment. If it's urgent, call{" "}
          <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
            {FIRM.phone}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-6 text-sm text-muted-foreground underline"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      id="enquiry"
      onSubmit={onSubmit}
      className="rounded-sm border border-border bg-card p-6 sm:p-8"
      noValidate
    >
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Six quick questions so we can prepare before we speak. Everyone in a granny flat arrangement
        needs their own advice — tell us which side you are on.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="gf-name">
            Your name
          </label>
          <input id="gf-name" name="name" className={`${field} mt-2`} autoComplete="name" required />
          {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-phone">
            Phone
          </label>
          <input
            id="gf-phone"
            name="phone"
            className={`${field} mt-2`}
            autoComplete="tel"
            inputMode="tel"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="gf-email">
            Email
          </label>
          <input
            id="gf-email"
            name="email"
            type="email"
            className={`${field} mt-2`}
            autoComplete="email"
            required
          />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>

        <div>
          <label className={labelCls} htmlFor="gf-relationship">
            Your role in the arrangement
          </label>
          <select
            id="gf-relationship"
            name="relationship"
            className={`${field} mt-2`}
            defaultValue="older-person"
          >
            {RELATIONSHIPS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-ageRange">
            Age of the older person
          </label>
          <select
            id="gf-ageRange"
            name="ageRange"
            className={`${field} mt-2`}
            defaultValue="70-79"
          >
            {AGE_RANGES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-fundingSource">
            Where is the money coming from?
          </label>
          <select
            id="gf-fundingSource"
            name="fundingSource"
            className={`${field} mt-2`}
            defaultValue="sale-of-home"
          >
            {FUNDING.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-contributionAmount">
            Approximate contribution{" "}
            <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="gf-contributionAmount"
            name="contributionAmount"
            className={`${field} mt-2`}
            placeholder="e.g. $350,000"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-propertySuburb">
            Property suburb <span className="text-muted-foreground">(if known)</span>
          </label>
          <input id="gf-propertySuburb" name="propertySuburb" className={`${field} mt-2`} />
        </div>
        <div>
          <label className={labelCls} htmlFor="gf-meetingPreference">
            How would you like to meet?
          </label>
          <select
            id="gf-meetingPreference"
            name="meetingPreference"
            className={`${field} mt-2`}
            defaultValue="in-person"
          >
            {MEETINGS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="gf-message">
            Anything else we should know?{" "}
            <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea id="gf-message" name="message" rows={4} className={`${field} mt-2`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : "Request a consultation"}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        Sending an enquiry does not create a lawyer–client relationship. Please don't include
        confidential information until we've confirmed we can act for you.
      </p>
    </form>
  );
}
